import { createClient, SupabaseClient } from "@supabase/supabase-js";

function readCookie(name: string): string | null {
  const match = document.cookie
    .split("; ")
    .find((r) => r.startsWith(name + "="));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

let cached: SupabaseClient | null = null;

export function supabase(): SupabaseClient {
  if (cached) return cached;
  cached = createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY,
    {
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
      global: {
        fetch: (input, init) => {
          const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
          // /auth/v1/* is GoTrue (Supabase Auth). It validates the bearer
          // token as a real user JWT and rejects anything without a `sub`
          // claim ("bad_jwt: invalid claim: missing sub claim"), which is
          // exactly the shape of bcdl_jwt. Skip the override there so
          // supabase-js can manage its own auth headers - admin flows
          // (signInWithPassword, mfa.verify, getSession) depend on it.
          const isAuthRoute = /\/auth\/v1(\/|$)/.test(url);
          const headers = new Headers(init?.headers ?? {});
          if (!isAuthRoute) {
            const jwt = readCookie("bcdl_jwt");
            if (jwt) headers.set("authorization", `Bearer ${jwt}`);
          }
          // No `credentials: "include"` here: this client authenticates via
          // the bearer header above, not cookies. Local Kong returns
          // `Access-Control-Allow-Origin: *` on /auth/v1 and /rest/v1, which
          // browsers refuse to pair with credentials-include. Edge function
          // calls that do need the session cookie go through the direct
          // fetch() calls in src/lib/auth.ts, not through this wrapper.
          return fetch(input, { ...init, headers });
        },
      },
    },
  );
  return cached;
}
